<template>
  <v-container class="my-3 px-md-7 px-0">
    <div v-if="loading || !dynamicReady">
      <v-skeleton-loader type="table-thead" />
      <v-skeleton-loader type="table-row-divider" />
      <v-skeleton-loader
        v-for="n in itemsPerPage"
        :key="n"
        type="table-row-divider"
      />
      <v-skeleton-loader type="table-tfoot" />
    </div>

    <v-data-table-server
      fixed-header
      height="70vh"
      class="elevation-0 my-light-table"
      v-else
      :headers="headersWithSelection"
      :items="dataFinal"
      :loading="loading || !dynamicReady"
      :sort-by="[{ key: sort, order: order }]"
      :hide-default-footer="true"
      :show-expand="hasExpand ? canExpand : false"
      item-value="id"
      @update:options="onUpdateOptions"
      hover
    >
      <template v-if="isSelectable" #item.__select="{ item }">
        <v-checkbox-btn
          :model-value="isSelected(item)"
          @update:model-value="toggleSelection(item, $event)"
        />
      </template>

      <!-- Acciones por fila -->
      <template #item.actions="{ item }">
        <v-menu>
          <template #activator="{ props }">
            <v-btn
              icon
              v-bind="props"
              variant="text"
              v-if="getAvailableActions(item).length > 0"
            >
              <v-icon>mdi-dots-horizontal</v-icon>
            </v-btn>
          </template>
          <v-list>
            <template
              v-for="action in getAvailableActions(item)"
              :key="action.onClick"
            >
              <v-list-item @click="handleRowAction(action.onClick, item, action)">
                <v-list-item-title>{{ action.label }}</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>

      <!-- Celdas -->
      <template
        v-for="header in headersFinal.filter(h => h.value !== 'actions' && h.value !== '__select' && h.value !== 'eye_detail')"
        :key="header.value"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <CellRenderer :moduleName="moduleName" :header="header" :item="item" />
      </template>

      <!-- Expandible -->
      <template v-slot:expanded-row="{ columns, item }" v-if="hasExpand">
        <tr>
          <td :colspan="columns.length" class="py-2">
            <component
              v-if="modRef?.DETAIL?.custom_expand_detail"
              :is="modRef?.DETAIL?.component"
              :item="item"
              :productable="getExpandData(item)"
              @refresh="emitRefresh"
            />
            <TableView
              v-else
              :headers="expandHeadersFinal"
              :data="getExpandData(item)"
              :is_detail="true"
              :expandActions="expandRowActions"
              :rowActionHandlers="rowActionHandlers"
            />
          </td>
        </tr>
      </template>

      <!-- Ojo de detalle -->
      <template v-if="hasDetail" v-slot:[`item.eye_detail`]="{ item }">
        <v-checkbox-btn
          v-if="isSelectable"
          :model-value="isSelected(item)"
          @update:model-value="toggleSelection(item, $event)"
        />
        <v-btn v-else icon class="elevation-0" size="small" @click="$emit('viewItem', item.id)">
          <v-icon>mdi-eye</v-icon>
        </v-btn>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, PropType, watch, getCurrentInstance, computed } from 'vue';
import { useLayoutField } from '@/composables/useLayoutField';
import { useFloatFormStore } from '@/store/floatForm';
import { useBottomSheetStore } from '@/store/bottomSheet';
import { configRegistry } from '@/lib/configRegistry';
import { get, getSubArray } from '@/utils/get';
import { useRoute } from 'vue-router';
import { useDialogsStore } from '@/store/dialogs';
import CellRenderer from '@/components/views/components/CellRenderer.vue';
import { loadModuleConfig, resolveViewName, getListPreset } from '@/lib/presets';

export default defineComponent({
  name: 'DataTableModule',
  components: { CellRenderer },
  props: {
    moduleName: { type: String, required: true },
    headers: { type: Array as PropType<any[]>, required: true },
    data: { type: Array as PropType<any[]>, required: true },
    onUpdate: Function as PropType<(data: Record<string, any>) => Promise<any>>,
    onDelete: Function as PropType<(data: Record<string, any>) => Promise<any>>,
    title: Object,
    rowActionHandlers: { type: Object, required: false, default: () => ({}) },
    is_detail: { type: Boolean, required: false },
    expandActions: { type: Array as PropType<any[]>, required: false },
    product_id: Number,
    selectable: { type: Boolean, required: false, default: false },
    selectedItems: {
      type: Array as PropType<Array<number | string>>,
      required: false,
      default: () => [],
    },
  },
  emits: ['update:selectedItems', 'viewItem'],
  setup(props) {
    const route = useRoute();

    // General
    const floatFormStore = useFloatFormStore();
    const bottomSheetStore = useBottomSheetStore();
    const loading = useLayoutField(props.moduleName, 'dataLoading');
    const itemsPerPage = useLayoutField(props.moduleName, 'itemsPerPage');
    const sort = useLayoutField(props.moduleName, 'sortBy');
    const order = useLayoutField(props.moduleName, 'direction');

    // Main Table State
    const hasExpand = ref(false);
    const expandHeaders = ref<any[]>([]);
    const expandKey = ref<string>('');
    const hasDetail = ref(false);
    const rowActions = ref<any[]>([]);
    const expandRowActions = ref<any[]>([]);
    const dynamicReady = ref(true);

    // Dynamic states
    const headersFinal = ref<any[]>([]);
    const dataFinal = ref<any[]>([]);
    const expandHeadersFinal = ref<any[]>([]);
    const modRef = ref<any>(null);

    // ---- Utils for dynamic columns ----
    function getDynamicColumnKeys(items: any, field: any, keyPath: any) {
      const allKeys = new Set();
      items.forEach((item: any) => {
        const arr = get(item, field) || [];
        arr.forEach((row: any) => {
          const key = get(row, keyPath);
          if (key) allKeys.add(key);
        });
      });
      return Array.from(allKeys);
    }

    function mergeHeadersWithDynamic(originalHeaders: any, dynamicHeaders: any) {
      const actionsIdx = originalHeaders.findIndex(
        (h: any) => h.value === 'actions' || h.align === 'end'
      );
      if (actionsIdx === -1) {
        return [...originalHeaders, ...dynamicHeaders];
      } else {
        return [
          ...originalHeaders.slice(0, actionsIdx),
          ...dynamicHeaders,
          ...originalHeaders.slice(actionsIdx),
        ];
      }
    }

    // ------ MAIN: calcula headers y dataFinal reactivo ------
    async function setupAll() {
      dynamicReady.value = false;

      const mod = await loadModuleConfig(props.moduleName);
      if (!mod) {
        headersFinal.value = [...props.headers];
        dataFinal.value = [...props.data];
        expandHeadersFinal.value = [];
        modRef.value = {};
        dynamicReady.value = true;
        return;
      }
      modRef.value = mod;

      // 👇 genérico: resuelve view + preset de acciones
      const viewName = resolveViewName(props.moduleName, route, mod);
      const preset = getListPreset(mod, viewName);

      rowActions.value = preset.rowActions || [];
      expandRowActions.value = mod.EXPAND_ROW_ACTIONS || [];

      // Main table logic
      if (mod.DYNAMIC_COLUMNS) {
        const { field, key, label, value, format } = mod.DYNAMIC_COLUMNS;
        const dynamicKeys = getDynamicColumnKeys(props.data, field, key);
        const dynamicHeaders = dynamicKeys.map((k: any) => ({
          title: typeof label === 'function' ? label(k) : k,
          value: `dynamic__${k.replace(/\s/g, '_').toLowerCase()}`,
          format,
        }));

        headersFinal.value = mergeHeadersWithDynamic(props.headers, dynamicHeaders);

        dataFinal.value = props.data.map((item) => {
          const arr = get(item, field) || [];
          const obj: Record<string, any> = {};
          dynamicKeys.forEach((k: any) => {
            const found = arr.find((row: any) => get(row, key) === k);
            obj[`dynamic__${k.replace(/\s/g, '_').toLowerCase()}`] = found ? found[value] : null;
          });
          return { ...item, ...obj };
        });
      } else {
        headersFinal.value = [...props.headers];
        dataFinal.value = [...props.data];
      }

      if (props.selectable) {
        headersFinal.value = headersFinal.value.map((h: any, idx: number) => {
          if (h?.value === 'eye_detail' || (!h?.title && idx === 0)) {
            return { ...h, value: '__select', sortable: false, title: '' };
          }
          return h;
        });
      }

      // Expand table logic
      hasExpand.value = !!mod.DETAIL?.expand_table;
      expandHeaders.value = mod.EXPAND_HEADERS || [];
      expandKey.value = mod.DETAIL?.key || '';
      hasDetail.value = mod.DETAIL?.eye_detail;

      // Dynamic expand columns
      if (mod.EXPAND_DYNAMIC_COLUMNS) {
        const allExpandData = props.data.flatMap(
          (item) => getSubArray(item, expandKey.value) || []
        );
        const { field, key, label, value, format } = mod.EXPAND_DYNAMIC_COLUMNS;
        const dynamicKeys = getDynamicColumnKeys(allExpandData, field, key);
        const dynamicHeaders = dynamicKeys.map((k: any) => ({
          title: typeof label === 'function' ? label(k) : k,
          value: `dynamic__${k.replace(/\s/g, '_').toLowerCase()}`,
          format,
        }));
        expandHeadersFinal.value = mergeHeadersWithDynamic(
          expandHeaders.value,
          dynamicHeaders
        );
      } else {
        expandHeadersFinal.value = [...expandHeaders.value];
      }

      dynamicReady.value = true;
    }

    // Run setup on mount and when data/headers/module cambian
    onMounted(setupAll);
    watch(
      () => [props.data, props.headers, props.moduleName, props.selectable],
      () => setupAll(),
      { deep: true }
    );

    // For each expand row, transforma los datos si hay columnas dinámicas
    function getExpandData(item: any) {
      const mod = modRef.value;
      const subArray = getSubArray(item, expandKey.value) || [];
      if (
        !mod?.EXPAND_DYNAMIC_COLUMNS ||
        !expandHeadersFinal.value.some((h: any) => h.value.startsWith('dynamic__'))
      ) {
        return subArray;
      }
      const { field, key, value } = mod.EXPAND_DYNAMIC_COLUMNS;
      const dynamicKeys = expandHeadersFinal.value
        .filter((h: any) => h.value.startsWith('dynamic__'))
        .map((h: any) => h.title.replace(/^Precio /, ''));
      return subArray.map((row: any) => {
        const arr = get(row, field) || [];
        const obj: Record<string, any> = {};
        dynamicKeys.forEach((k) => {
          const found = arr.find((inner: any) => get(inner, key) === k);
          obj[`dynamic__${k.replace(/\s/g, '_').toLowerCase()}`] = found ? found[value] : null;
        });
        return { ...row, ...obj };
      });
    }

    // --- el resto igual que antes ---
    const onUpdateOptions = (options: any) => {
      const sortObj = options?.sortBy?.[0];
      if (sortObj?.key && sortObj?.order) {
        sort.value = sortObj.key;
        order.value = sortObj.order;
      } else {
        sort.value = '';
      }
    };

    const editItem = (item: any) => {
      floatFormStore.openForm({
        component: 'EditForm',
        formTitle: 'Editar ' + (props.title as any)?.singular,
        formWidth: '1080',
        module: props.moduleName,
        props: {
          item,
          onUpdate: props.onUpdate,
          initialValues: {
            ...(props.product_id ? { product_id: props.product_id } : {}),
          },
        },
      });
    };

    const deleteItem = (item: any) => {
      bottomSheetStore.openSheet({
        module: props.moduleName,
        formTitle: `¿Seguro(a) que deseas eliminar ${
          (props.title as any)?.singular + ' ' + item.id
        }?`,
        props: {
          item,
          message: 'Esta acción no se puede deshacer.',
          action: (item) => {
            if (!props.onDelete) {
              console.warn('No se proporcionó onDelete');
              return Promise.reject(new Error('No se proporcionó onDelete'));
            }
            return props.onDelete(item.id);
          },
        },
      });
    };

    function handleRowAction(actionName: any, item: any, action?: any) {
      if (actionName === '__edit') return editItem(item);
      else if (actionName === '__delete') return deleteItem(item);
      else if (props.rowActionHandlers && typeof props.rowActionHandlers[actionName] === 'function') {
        props.rowActionHandlers[actionName](item, action);
      } else {
        console.warn('No handler for action:', actionName);
      }
    }

    const canExpand = (item: any) => {
      const data = get(item, expandKey.value);
      return !!data && (Array.isArray(data) ? data.length > 0 : true);
    };

    // permisos y showIf
    const instance = getCurrentInstance();
    const $can = instance?.appContext.config.globalProperties
      .$can as (perm: string, item?: any) => boolean;

    function byPath(obj: any, path?: string) {
      if (!obj || !path) return undefined;
      return path.split('.').reduce((a, k) => (a ? a[k] : undefined), obj);
    }
    function passesShow(a: any, item: any) {
      if (typeof a?.showIf === 'function') return !!a.showIf(item);
      if (a?.showWhen) return !!byPath(item, a.showWhen);
      return true;
    }

    const getAvailableActions = (item: any) => {
      const actions = props.is_detail ? props.expandActions || [] : rowActions.value || [];
      return actions
        .filter((a: any) => a.permission === undefined || ($can && $can(a.permission, item)))
        .filter((a: any) => passesShow(a, item));
    };

    // detalle (diálogo)
    const dialogsStore = useDialogsStore();
    const openDetailDialog = async (id: any, moduleName: string) => {
      const loader = (configRegistry as any)[moduleName];
      let component: any;
      if (loader) {
        const mod = await loader();
        component = mod?.DETAIL?.component;
      } else {
        console.warn(`[${moduleName}] No se encontró DETAIL.component en configRegistry`);
        return;
      }
      dialogsStore.open(moduleName, id, { id });
    };

    // no-op para @refresh en custom_expand_detail
    const emitRefresh = () => {};

    const emit = instance?.emit;
    const selectedRowsModel = computed<Array<number | string>>({
      get: () => props.selectedItems || [],
      set: (items) => emit?.('update:selectedItems', items),
    });

    const isSelectable = computed(() => !!props.selectable);

    const usesEyeDetailAsSelection = computed(() => {
      if (!isSelectable.value || !hasDetail.value) return false;
      return headersFinal.value.some((h: any) => h?.value === 'eye_detail');
    });

    const headersWithSelection = computed(() => {
      if (!isSelectable.value) return headersFinal.value;
      if (usesEyeDetailAsSelection.value) return headersFinal.value;
      const alreadyHasSelection = headersFinal.value.some((h: any) => h?.value === '__select');
      if (alreadyHasSelection) return headersFinal.value;
      return [{ title: '', value: '__select', sortable: false, width: 48 }, ...headersFinal.value];
    });

    const isSelected = (item: any) => {
      const id = Number(item?.id);
      return selectedRowsModel.value.map(Number).includes(id);
    };

    const toggleSelection = (item: any, checked: boolean) => {
      const id = Number(item?.id);
      const current = selectedRowsModel.value.map(Number);
      const next = checked
        ? Array.from(new Set([...current, id]))
        : current.filter((v) => v !== id);
      selectedRowsModel.value = next;
    };

    return {
      // state/ui
      sort,
      order,
      loading,
      itemsPerPage,
      dynamicReady,
      headersFinal,
      dataFinal,
      hasExpand,
      expandHeadersFinal,
      expandKey,
      hasDetail,
      modRef,
      headersWithSelection,

      // actions
      getAvailableActions,
      handleRowAction,
      canExpand,
      getExpandData,
      emitRefresh,
      selectedRowsModel,
      isSelected,
      toggleSelection,
      isSelectable,

      onUpdateOptions
    };
  },
});
</script>

<style scoped>
.v-theme--light .my-light-table {
  background-color: rgb(255, 255, 255);
}
</style>
